import { useState, useRef, useCallback, useEffect } from 'react';
import {
  areAllSectionsSelected,
  getAllSectionIds,
  toggleId,
} from './export-plan-section.util';

const resetSelectionState = (sections) => getAllSectionIds(sections);

export const useExportPlanModalHook = ({ open = false, onClose, onExport, sections = [] }) => {
  const [selectedIds, setSelectedIds] = useState(() => resetSelectionState(sections));
  const [isEmailStepOpen, setIsEmailStepOpen] = useState(false);
  const modalRef = useRef(null);
  const closingFromUserRef = useRef(false);
  const wasOpenRef = useRef(false);

  const isSectionStepOpen = open && !isEmailStepOpen;
  const isAllSelected = areAllSectionsSelected(selectedIds, sections);
  const hasSelectedSections = selectedIds.length > 0;

  const resetFlowState = useCallback(() => {
    setSelectedIds(resetSelectionState(sections));
    setIsEmailStepOpen(false);
  }, [sections]);

  const handleClose = useCallback(() => {
    closingFromUserRef.current = true;
    if (modalRef.current?.toggleModal) {
      modalRef.current.toggleModal();
    }
    resetFlowState();
    onClose?.();
  }, [onClose, resetFlowState]);

  const handleEmailClose = useCallback(() => {
    setIsEmailStepOpen(false);
    resetFlowState();
    onClose?.();
  }, [onClose, resetFlowState]);

  useEffect(() => {
    if (!open) {
      resetFlowState();
      return;
    }

    setSelectedIds(resetSelectionState(sections));
  }, [open, sections, resetFlowState]);

  const syncSectionStepModal = useCallback(() => {
    if (!modalRef.current?.toggleModal) {
      return;
    }

    if (isSectionStepOpen) {
      closingFromUserRef.current = false;
      wasOpenRef.current = true;
      modalRef.current.toggleModal();
      return;
    }

    if (wasOpenRef.current && !closingFromUserRef.current) {
      wasOpenRef.current = false;
      if (modalRef.current?.toggleModal) {
        modalRef.current.toggleModal();
      }
    } else {
      wasOpenRef.current = false;
      closingFromUserRef.current = false;
    }
  }, [isSectionStepOpen, handleClose]);

  useEffect(syncSectionStepModal, [syncSectionStepModal]);

  const handleToggleSection = useCallback((sectionId) => {
    setSelectedIds((previous) => toggleId(previous, sectionId));
  }, []);

  const handleToggleSelectAll = useCallback(() => {
    setSelectedIds(isAllSelected ? [] : getAllSectionIds(sections));
  }, [isAllSelected, sections]);

  const handleExportPlanClick = useCallback(() => {
    if (!hasSelectedSections) {
      return;
    }

    setIsEmailStepOpen(true);
  }, [hasSelectedSections]);

  const handleEmailExport = useCallback(
    async ({ emails, password }) => {
      try {
        await onExport?.({
          sectionIds: selectedIds,
          emails,
          password,
        });
        resetFlowState();
        onClose?.();
      } catch {
        // Keep the email modal open when export fails.
      }
    },
    [onExport, onClose, resetFlowState, selectedIds],
  );

  return {
    modalRef,
    selectedIds,
    isSectionStepOpen,
    isEmailStepOpen,
    isAllSelected,
    hasSelectedSections,
    handleToggleSection,
    handleToggleSelectAll,
    handleExportPlanClick,
    handleEmailExport,
    handleClose,
    handleEmailClose,
  };
};
